// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';
import React, { useState, useCallback, useEffect } from 'react';
import { Input, InputProps } from 'reactstrap';

/** Props interface for InputFilter component */
export interface InputFilterProps {
  id: string;
  placeholder?: string;
  value: string;
  label?: string;
  maxLength?: number;
  enableDebounce?: boolean;
  debounceTime?: number;
  labelWidth?: string;
  onSelectedValueChange: (value: string) => void;
  onClear?: () => void;
  inputProps?: InputProps;
}

interface SearchInputProps extends InputFilterProps {
  onFilterClick?: () => void;
}

/** InputFilter Component for rendering text inputs with optional debouncing */
export const InputFilter: React.FC<InputFilterProps> = ({
  id,
  placeholder,
  value,
  label,
  maxLength,
  labelWidth,
  enableDebounce = false,
  debounceTime = 300,
  onSelectedValueChange,
  inputProps,
}) => {
  const [inputValue, setInputValue] = useState(value);

  const debouncedUpdate = useCallback(
    debounce((newValue: string) => {
      onSelectedValueChange(newValue);
    }, debounceTime),
    [debounceTime, onSelectedValueChange],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (enableDebounce) {
      debouncedUpdate(newValue);
    } else {
      onSelectedValueChange(newValue);
    }
  };

  const inputElement = (
    <Input
      key={id}
      type="text"
      placeholder={placeholder}
      value={inputValue}
      maxLength={maxLength}
      onChange={handleChange}
      className={`form-control${label ? ' border-0' : ''}`}
      style={{ backgroundColor: '#ffffff' }}
      width="100px"
      {...inputProps}
    />
  );

  return (
    <>
      {label ? (
        // <div className="d-flex flex-wrap" style={{ gap: '5px' }}>
        <div
          className="flex-grow-1 d-flex border align-items-center border border-light rounded bg-light"
          style={{ flexBasis: '45%' }}
        >
          <div
            className="text-nowrap align-items-center text-dark px-2 font-weight-bold"
            style={
              labelWidth ? { width: labelWidth, maxWidth: labelWidth } : {}
            }
          >
            {label}
          </div>
          {inputElement}
        </div>
      ) : (
        inputElement
      )}
    </>
  );
};

export const SearchInputFilter: React.FC<SearchInputProps> = ({
  id,
  placeholder,
  value,
  maxLength,
  enableDebounce = false,
  debounceTime = 300,
  onClear,
  onSelectedValueChange,
  inputProps,
  onFilterClick,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  // reset the value if the props value change
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const debouncedUpdate = useCallback(
    debounce((newValue: string) => {
      onSelectedValueChange(newValue);
    }, debounceTime),
    [debounceTime, onSelectedValueChange],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (enableDebounce) {
      debouncedUpdate(newValue);
    } else {
      onSelectedValueChange(newValue);
    }
  };

  const handleSearchFocus = () => {
    setIsFocused(true);
  };

  const handleSearchBlur = () => {
    setIsFocused(false);
  };

  const clearInput = () => {
    setInputValue('');
    if (onClear) {
      onClear();
    }
  };

  const inputElement = (
    <Input
      key={id}
      type="text"
      placeholder={placeholder}
      value={inputValue}
      maxLength={maxLength}
      onChange={handleChange}
      className="form-control border-0"
      onFocus={handleSearchFocus}
      onBlur={handleSearchBlur}
      style={{ height: 'auto', fontSize: '0.85rem' }}
      {...inputProps}
    />
  );

  return (
    <div
      className={`d-flex align-items-center border ${
        isFocused ? 'border-primary' : 'border-light'
      } rounded shadow-sm px-2 py-0`}
      style={{
        cursor: 'pointer',
        transition: 'border-color 0.3s ease',
        position: 'relative',
      }}
    >
      {inputElement}
      {inputValue && (
        <i
          className="fas fa-times text-muted"
          style={{
            position: 'relative',
            right: '0.5rem',
            cursor: 'pointer',
            fontSize: '1.2rem',
          }}
          onClick={clearInput}
        ></i>
      )}
      {/* Filter Icon */}
      <i
        className="fas fa-sliders-h ml-2 text-primary"
        style={{ fontSize: '1rem', marginRight: '0.5rem' }}
        onClick={() => {
          onFilterClick && onFilterClick();
        }}
      ></i>
      {/* Search Icon */}
      <i
        className="fas fa-search ml-2 text-primary"
        style={{ fontSize: '1rem' }}
        onClick={() => {
          onFilterClick && onFilterClick();
        }}
      ></i>
    </div>
  );
};
